/**
 * Search controller.
 *
 * Owns query/filter/pagination state and the search call itself, so the
 * standalone page and the embedded overlay share one implementation and cannot
 * drift in ranking, filter handling or paging behaviour.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSelectedLanguage } from '../components/LanguageSelector';
import { apiService } from '../services';
import { useAuth } from '../auth';
import { getFacetValues, useFacets } from './useFacets';
import type { SearchFilters, SearchResponse } from '../types/search';

export const PAGE_SIZE = 20;

export function useSearchController() {
  const { accessToken } = useAuth();
  const { facets } = useFacets();

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);

  // Keep refs so callbacks always read the latest values without re-binding.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    apiService.setAccessToken(accessToken);
  }, [accessToken]);

  const executeSearch = useCallback(
    async (searchQuery: string, searchFilters: SearchFilters, fromOffset = 0) => {
      if (!searchQuery.trim()) return;

      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      // Optimize filters: remove filters where all values are selected
      // (equivalent to no filter)
      const optimizedFilters: SearchFilters = {};
      for (const [key, values] of Object.entries(searchFilters)) {
        if (!values || !Array.isArray(values) || values.length === 0) continue;
        // zenon_version is exempt: an absent version filter means "latest" on the
        // backend, not "all versions". Dropping a fully-selected version filter
        // would silently narrow the search to a single release.
        const facet =
          key === 'zenon_version' ? undefined : facets.find((f) => f.field_name === key);
        if (facet) {
          const allValues = getFacetValues(facet);
          if (values.length < allValues.length) {
            optimizedFilters[key] = values;
          }
        } else {
          optimizedFilters[key] = values;
        }
      }

      try {
        const response = await apiService.search({
          query: searchQuery,
          filters: optimizedFilters,
          size: PAGE_SIZE,
          from: fromOffset,
          use_semantic: true,
          user_language: getSelectedLanguage(),
        });
        setResults(response);
      } catch (err) {
        console.error('Search error:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while searching. Please try again.'
        );
        setResults(null);
      } finally {
        setIsLoading(false);
      }
    },
    [facets]
  );

  // Reconcile selected filters against the currently visible facet values.
  // When the auth identity changes (e.g. logout), the backend stops returning
  // previously-visible values (e.g. `source=sunrise` is hidden from anonymous
  // users) and useFacets refetches. Drop any selected value that is no longer
  // present so dangling filter chips disappear, then re-run the active search.
  useEffect(() => {
    if (facets.length === 0) return; // facets not loaded yet — don't wipe selection
    const prev = filtersRef.current;
    let changed = false;
    const next: SearchFilters = {};
    for (const [key, value] of Object.entries(prev)) {
      // Only array-valued (facet) filters are reconciled; date-range string
      // filters and other scalar fields are passed through untouched.
      if (!Array.isArray(value)) {
        next[key] = value;
        continue;
      }
      const facet = facets.find((f) => f.field_name === key);
      if (!facet) {
        // No matching facet field in the visible set — keep as-is to avoid
        // clobbering dynamic fields not represented in the facet response.
        next[key] = value;
        continue;
      }
      const visible = new Set(facet.values.map((v) => v.value));
      const filtered = value.filter((v) => visible.has(v));
      if (filtered.length !== value.length) changed = true;
      if (filtered.length > 0) next[key] = filtered;
    }
    if (!changed) return;
    setFilters(next);
    filtersRef.current = next;
    if (queryRef.current.trim()) {
      setPage(1);
      executeSearch(queryRef.current, next, 0);
    }
  }, [facets, executeSearch]);

  // Re-execute search when the content language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      if (queryRef.current.trim()) {
        setPage(1);
        executeSearch(queryRef.current, filtersRef.current, 0);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, [executeSearch]);

  /** Run a new search from page 1 using the latest filters. */
  const search = useCallback(
    (searchQuery: string) => {
      setPage(1);
      executeSearch(searchQuery, filtersRef.current, 0);
    },
    [executeSearch]
  );

  /** Apply new filters and re-run the active query. */
  const applyFilters = useCallback(
    (newFilters: SearchFilters) => {
      setFilters(newFilters);
      filtersRef.current = newFilters;
      setPage(1);
      if (queryRef.current.trim()) {
        executeSearch(queryRef.current, newFilters, 0);
      }
    },
    [executeSearch]
  );

  const goToPage = useCallback(
    (newPage: number) => {
      setPage(newPage);
      executeSearch(queryRef.current, filtersRef.current, (newPage - 1) * PAGE_SIZE);
    },
    [executeSearch]
  );

  const reset = useCallback(() => {
    setQuery('');
    setFilters({});
    setResults(null);
    setError(null);
    setHasSearched(false);
    setPage(1);
  }, []);

  const totalPages = results ? Math.ceil(results.total / PAGE_SIZE) : 0;

  return {
    query,
    setQuery,
    filters,
    setFilters,
    filtersRef,
    results,
    isLoading,
    error,
    hasSearched,
    page,
    totalPages,
    search,
    applyFilters,
    goToPage,
    reset,
  };
}
